import { Injectable } from '@angular/core';
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { SigningBillingContactBody } from "@models/billing-signing/signing-billing-contact-body";
import { UpdateBillingContactModel } from "@models/billing-signing/update-billing-contact-model";
import { Company } from "@models/companies/company";
import { CompanyRecord } from "@models/companies/company-record";
import { OfficerRecord } from "@models/relationships/officer-record";
import { PartnerAndManagerChangeRequest } from "@models/relationships/partner-and-manager-change-request";
import { ConfirmationRequiredResponse } from "@models/requests/confirmation-required-response";
import { RecentSignDocument } from "@models/ui-models/files";
import BaseApiService from "@shared/services/base/base-api.service";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { map, Observable, tap } from "rxjs";

@Injectable({ providedIn: 'root' })
export class CompaniesApiService extends BaseApiService {
  getById(id: string, applyIncompleteDocuments = true, excludedDocumentId = ''): Observable<Company> {
    const options = { params: { excludedDocumentId } };
    return this.api.get<Company>(`${ this.apiUrl }/companies/${ id }?applyIncompleteDocuments=${ applyIncompleteDocuments }`, options)
      .pipe(
        map((data) => new Company(data))
      );
  }

  updateDetails(id: string, company: Company) {
    const updateRequest = {
      entityId: company.entityId,
      corporateKey: company.corporateKey,
      nextAnnualReviewDate: DatepickerHelper.buildDateString(company.nextAnnualReviewDate),
      companyType: company.companyType,
      referenceNumber: company.referenceNumber,
      familyGroup: company.familyGroup,
      accountManagerId: company.accountManagerId,
      partnerManagerId: company.partnerManagerId,
    };

    return this.api.put<void>(`${ this.apiUrl }/companies/${ id }/details`, updateRequest, { headers: { 'Content-Type': 'application/json' } });
  }

  generateFakeAnnualStatement(id: string) {
    return this.api.get<string>(`${ this.apiUrl }/companies/generate-annual-statement-f/${ id }`);
  }

  generatePayslip(companyId: string, payload): Observable<string> {
    return this.api.post<string>(`${ this.apiUrl }/companies/${ companyId }/payslip`, payload);
  }

  updateSigningBillingContact(companyId: string, body: SigningBillingContactBody): Observable<unknown> {
    return this.api.post<unknown>(`${ this.apiUrl }/companies/${ companyId }/signing-billing-contact`, body);
  }

  setBillingContact(companyId: string, updateBillingContactModel: UpdateBillingContactModel): Observable<void> {
    return this.api.put<void>(`${ this.apiUrl }/companies/${ companyId }/billing-contact`, updateBillingContactModel);
  }

  setSigningContact(companyId: string, signingContactId: string): Observable<void> {
    const body = JSON.stringify(signingContactId);
    return this.api.put<void>(`${ this.apiUrl }/companies/${ companyId }/signing-contact`, body, { headers: { 'Content-Type': 'application/json' } });
  }

  changePartnerAndAccountManager(body: PartnerAndManagerChangeRequest): Observable<unknown> {
    return this.api.put<unknown>(`${ this.apiUrl }/companies/partner-manager`, body);
  }

  hasPendingChanges(companyId: string): Observable<boolean> {
    const params = { params: { companyId } };
    return this.api.get<boolean>(`${ this.apiUrl }/companies/has-pending-changes`, params);
  }

  list() {
    return this.api.get<CompanyRecord[]>(`${ this.apiUrl }/companies`);
  }

  refreshList(force = true): Observable<ConfirmationRequiredResponse> {
    return this.api.post<ConfirmationRequiredResponse>(`${ this.apiUrl }/companies/list/refresh`, {}, { params: { force } });
  }

  listNames(): Observable<Map<string, string>> {
    return this.api.get<Record<string, string>>(`${ this.apiUrl }/companies/names`)
      .pipe(
        map((data: Record<string, string>) => {
          const resultMap = new Map<string, string>();

          Object.keys(data).forEach(key => {
            resultMap.set(key, data[key]);
          });

          return resultMap;
        }));
  }

  getOfficeholders(companyId: string): Observable<OfficerRecord[]> {
    const params = { params: { companyId } };

    return this.api.get<OfficerRecord[]>(`${ this.apiUrl }/companies/get-officeholders`, params)
      .pipe(
        map((officerRecords) => {
            return officerRecords
              .filter(Boolean)
              .map((officerRecord) => new OfficerRecord(officerRecord));
          }
        )
      );
  }

  getRecentDocuments(companyId: string, status: SigningStatus = SigningStatus.ReadyToSend): Observable<RecentSignDocument[]> {
    return this.api.get<RecentSignDocument[]>(`${ this.apiUrl }/companies/${ companyId }/recent-documents/${ status }`);
  }

  refreshDebts(companyId: string): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/companies/${ companyId }/debts/refresh`, null);
  }

  refreshDebtsBulk(companiesId: string[]): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/companies/bulk/debts/refresh`, companiesId);
  }

  refreshInfo(companyId: string): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/companies/${ companyId }/info/refresh`, null);
  }

  refreshInfoBulk(companiesId: string[]): Observable<void> {
    return this.api.post<void>(`${ this.apiUrl }/companies/bulk/info/refresh`, companiesId);
  }

  updatePartnerManager(companyId: string, partnerManagerId: string): Observable<void> {
    return this.api.put<void>(`${ this.apiUrl }/companies/${ companyId }/details/partner-manager`, JSON.stringify(partnerManagerId), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  generatePdfProfile(companyId: string, fileName: string, applyIncompleteDocuments = false) {
    const params = { params: { applyIncompleteDocuments } };
    return this.api.get(`${ this.apiUrl }/companies/${ companyId }/profile/pdf`, { responseType: 'blob' as 'json', ...params })
      .pipe(
        tap({
          next: (blob: any) => {
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);
          }
        })
      );
  }
}
